import type { AuditStatus } from './types';
import {
  AUDIT_STATUS_LABEL,
  AUDIT_STATUS_ORDER,
  AUDIT_STATUS_STEP_HINT,
} from './auditUtils';

type Props = {
  status: AuditStatus;
  canAdvance?: boolean;
  advancing?: boolean;
  nextStatus?: AuditStatus;
  onAdvance?: () => void;
};

export function AuditStatusStepper({
  status,
  canAdvance = false,
  advancing = false,
  nextStatus,
  onAdvance,
}: Props) {
  const currentIndex = AUDIT_STATUS_ORDER.indexOf(status);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4">
      <div className="flex w-full items-center">
        {AUDIT_STATUS_ORDER.map((step, index) => {
          const isPast = index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <div key={step} className="flex flex-1 items-center last:flex-none">
              <div className="flex min-w-0 flex-col items-center gap-1 text-center">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold ${
                    isPast
                      ? 'border-green-600 bg-green-600 text-white'
                      : isCurrent
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted text-muted-foreground'
                  }`}
                  aria-current={isCurrent ? 'step' : undefined}
                  title={AUDIT_STATUS_LABEL[step]}
                >
                  {isPast ? '✓' : index + 1}
                </div>
                <div className="hidden min-w-[5.5rem] sm:block">
                  <p
                    className={`text-xs font-medium leading-tight ${
                      isCurrent ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {AUDIT_STATUS_LABEL[step]}
                  </p>
                  <p className="text-[10px] leading-tight text-muted-foreground">
                    {AUDIT_STATUS_STEP_HINT[step]}
                  </p>
                </div>
              </div>
              {index < AUDIT_STATUS_ORDER.length - 1 && (
                <div
                  className={`mx-1 h-0.5 flex-1 sm:mx-2 ${
                    index < currentIndex ? 'bg-green-600' : 'bg-border'
                  }`}
                  aria-hidden
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="sm:hidden text-center text-sm">
        <span className="font-medium">{AUDIT_STATUS_LABEL[status]}</span>
        <span className="text-muted-foreground"> — {AUDIT_STATUS_STEP_HINT[status]}</span>
      </div>

      {canAdvance && nextStatus && onAdvance && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <p className="text-sm text-muted-foreground">
            Siguiente paso:{' '}
            <span className="font-medium text-foreground">{AUDIT_STATUS_LABEL[nextStatus]}</span>
          </p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={advancing}
            onClick={onAdvance}
          >
            {advancing ? 'Avanzando…' : `Avanzar a ${AUDIT_STATUS_LABEL[nextStatus]}`}
          </button>
        </div>
      )}

      {status === 'cerrada' && (
        <p className="border-t border-border pt-4 text-sm text-muted-foreground">
          Esta auditoría está cerrada. Los datos quedan en modo consulta.
        </p>
      )}
    </div>
  );
}
